/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// import React
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import FormHelperText from '@mui/material/FormHelperText';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Icon from "@mui/material/Icon";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import propertiesData from 'layouts/leaseInvoices/data/leaseInvoiceData';
import EditInvoice from "examples/EditInvoiceDialog";

import LinearProgress from '@mui/material/LinearProgress';
import * as XLSX from 'xlsx'; // Use named imports for xlsx
// import LeaseInvoiceService from "services/lease-invoice-service"; // Import LeaseInvoiceService
const ylcId = "2800c706-805e-4cd9-9a64-90df329827b3";
const yl3Id = "c96a2dcb-58c7-4008-9b0d-43c3ee073822";
const gibId = "0ac1e107-d227-45db-a9d6-ca9afd806331";
// Helpers
function Payments () {
  const [errors, setErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tenantCode, setTenantCode] = useState('');
  const [inputValue, setInputValue] = useState("");
  const [filter, setFilter] = useState('');
  const [isShow, setIsShow] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [errorEdit, setErrorEdit] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [pageCustom, setPageCustom] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedValue, setSelectedValue] = useState(gibId);
  const [excelData, setExcelData] = useState([]);
  const [isExcelPopupOpen, setIsExcelPopupOpen] = useState(false);
  const isSearched = useRef(false);
  const currentPropertyCode = useRef(gibId);
  

  const { columns, rows, getPropertyLeasesList, getInvoiceList, innerLoading, buttonDisabled, setButtonDisabled, open, setOpen, isShowDialog, sortField, sortDirection, setSortField, setSortDirection, editedRow, totalPages, totalRows } = propertiesData();
  // console.log("editedRow", editedRow);

  const onChangeText = useCallback((event) => {
    const { value }  = event.target
    if (value.length < 3) {
      setErrorMessage('Tenant code value must be more equal than 3 characters');
      setErrors(true);
    } else {
      setErrorMessage('');
      setErrors(false);
    }
    setInputValue(value);
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenNotification(false);
  }, []);

  const onHandleSubmit = useCallback((event) => {
    event.preventDefault();
    setFilter(inputValue);
    isSearched.current = true;
    setPageCustom(0);
  }, [inputValue]);

  const handleCloseExcelPopup = () => {
    setIsExcelPopupOpen(false);
  };

  const handleConfirmDownload = () => {
    if (excelData.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(excelData); // Convert array of objects to worksheet
      const workbook = XLSX.utils.book_new(); // Create a new workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices"); // Append the worksheet
      XLSX.writeFile(workbook, "PropertyLeases.xlsx"); // Write the workbook to a file
      setIsExcelPopupOpen(false); // Close the popup after download
    } else {
      console.error("Excel data is empty, cannot generate file.");
    }
  };

  const onChangeSelect = (event) => {
    const { value, name } = event.target;
    if (name === "property_code") {
      // console.log("value", value);
      setSelectedValue(value);
      if (filter !== "") {
        setFilter('');
        setInputValue('');
      }
    }
  }

  useEffect(() => {
    // console.log("selectedValue", selectedValue);
    // console.log("currentPropertyCode", currentPropertyCode.current);
    let currentPage = pageCustom + 1;
    // If the selected value is different from the current property code, reset the page to 0
    if (selectedValue !== currentPropertyCode.current) {
      currentPage = 1;
      currentPropertyCode.current = selectedValue;
      if (pageCustom !== 0) {
        setPageCustom(0); // Trigger lại useEffect nếu cần
        return; // Ngăn gọi API ngay
      }
    }
    getInvoiceList(currentPage, rowsPerPage, filter, sortField, sortDirection, selectedValue);
  },[selectedValue, pageCustom, rowsPerPage, filter]);

  useEffect(() => {
    // console.log("open", open);
    if (open) {
      document.documentElement.style.overflow = "hidden"; // html
    } else {
      document.documentElement.style.overflow = "";
    }

    return () => {
      // Cleanup nếu component bị unmount
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    // Refresh invoice list when duplication is completed
    if (isEdited) {
      let currentPage = pageCustom + 1;
      getInvoiceList(currentPage, rowsPerPage, filter, sortField, sortDirection, selectedValue);
      setIsEdited(false); // Reset flag after refresh
    }
  }, [isEdited]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Invoices
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2} sx={{ padding: "19px 20px"}}>
                  <Grid item xs={9}>
                    <MDBox
                        display="flex"
                        flexDirection="column"
                        gap={3}
                        component="form"
                        role="form"
                        method="POST"
                        onSubmit={onHandleSubmit}
                    >
                        <MDBox
                            display="flex"
                            alignItems="center"
                            gap={2}
                            justifyContent="space-between"
                            flexWrap="nowrap"
                        >
                            <MDInput
                                type="search"
                                label="Filter"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                sx={{
                                    "& .MuiInput-underline:after": { borderBottomColor: "#388e3c"},
                                    "& .MuiInputLabel-root.Mui-focused": { color: "#388e3c" }
                                }}
                                value={inputValue}
                                onChange={onChangeText}
                                name="tenant_code"
                            />
                            <MDButton
                                variant="gradient"
                                sx={{
                                    padding: "12px 30px",
                                    backgroundColor: "#388e3c",
                                    color: "#ffffff",
                                    borderRadius: "0.2rem",
                                    "&:hover": {
                                    backgroundColor: "#919191",
                                    color: "#fff",
                                    },
                                    "&:disabled": {
                                    background: "#e0e0e0"
                                    }
                                }}
                                disabled={ buttonDisabled }
                                type="submit"
                            >
                                Search
                            </MDButton>
                        </MDBox>
                        <MDBox mb={2}>
                          <FormControl width="50%">
                              <InputLabel
                                  id="property-code-select-label"
                              >
                                  Property code
                              </InputLabel>
                              <Select
                                  labelId="property-code-select-label"
                                  name="property_code"
                                  value={selectedValue}
                                  onChange={onChangeSelect}
                                  displayEmpty
                                  label="Property code"
                                  sx={{
                                      minWidth: 200,
                                      height: '44px', // Set chiều cao cố định
                                  }}
                                  MenuProps={{
                                  disableScrollLock: true, // Ngăn MUI tự động thêm padding-right vào <body>
                                  }}
                              >
                                  <MenuItem value={gibId}>GIB</MenuItem>
                                  <MenuItem value={ylcId}>YLC</MenuItem>
                                  <MenuItem value={yl3Id}>YL3</MenuItem>
                              </Select>
                          </FormControl>
                        </MDBox>
                    </MDBox>
                  </Grid>
                  {/* <Grid item xs={3}>
                    <MDButton
                      variant="gradient"
                      sx={{
                        padding: "12px 30px",
                        backgroundColor: "#388e3c",
                        color: "#ffffff",
                        borderRadius: "0.2rem",
                        "&:hover": {
                          backgroundColor: "#919191",
                          color: "#fff",
                        },
                      }}
                      onClick={handleDownloadExcel}
                    >
                      Download Report
                    </MDButton>
                  </Grid> */}
                  <Grid item xs={12}>
                    <MDBox>
                      <DataTable
                          table={{ columns, rows }}
                          isSorted={false}
                          showTotalEntries={false}
                          entriesPerPage={false}
                          isPaginated={true}
                          pageCustom={pageCustom}
                          setPageCustom={setPageCustom}
                          totalPages={totalPages}
                          totalRows={totalRows}
                          rowsPerPage={rowsPerPage}
                          setRowsPerPage={setRowsPerPage}
                          innerLoading={innerLoading}
                          isShow={isShow}
                          noEndBorder
                      />
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        { isShowDialog && (
          <EditInvoice
            open={open}
            setOpen={setOpen}
            setOpenNotification={setOpenNotification}
            rowData={editedRow}
            setIsShow={setIsShow}
            setIsEdited={setIsEdited}
            setErrorEdit={setErrorEdit}
          />
        )}
        {isExcelPopupOpen && (
          <MDBox
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#001f3f", // Dark blue background
              color: "#ffffff", // White text color
              zIndex: 1300, // Ensure it appears above other elements
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
              width: "270px", // Set a fixed width
            }}
          >
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" color="black">Excel Report</MDTypography>
                <MDBox display="flex" justifyContent="space-between" mt={2}>
                  <MDButton variant="outlined" color="error" onClick={handleCloseExcelPopup}>
                    Cancel
                  </MDButton>
                  <MDButton variant="gradient" color="success" onClick={handleConfirmDownload}>
                    Download
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
        <Snackbar open={openNotification} autoHideDuration={4000} onClose={handleClose}>
          { errorEdit ? (
            <Alert
            onClose={handleClose}
            severity="error"
            variant="filled"
            sx={{ width: '100%', color: '#ffffff' }}
          >
            Update Invoice Unsuccessfully !
          </Alert>
          ) : (
            <Alert
              onClose={handleClose}
              severity="success"
              variant="filled"
              sx={{ width: '100%', color: '#ffffff' }}
            >
              Update Invoice Successfully !
            </Alert>
          )}
      </Snackbar>
    </DashboardLayout>
  );
}
export default Payments;
